import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import ProductCard from "../components/ProductCard";
import { Check, Sparkles, ArrowRight, Gift } from "lucide-react";

const TRUST_BADGES = [
    { title: "Lab Tested", sub: "Lab Tests purity" },
    { title: "Clinically", sub: "Reviewed" },
    { title: "Evidence-Based", sub: "Ingredients" },
    { title: "GMP Certified", sub: "Manufacturing" },
];

const DOCTORS = [
    { name: "Dr. Priya Kapoor", spec: "Meta & Inner" },
    { name: "Dr. Amit Verma", spec: "Men's Skin" },
    { name: "Dr. Neha Sharma", spec: "Skin Guidance" },
];

// Three signature kit palettes — lavender, light green, complementary peach
const KIT_PALETTES = [
    { bg: "#efe5fb", border: "#c9b5ee", accent: "#6b4ea0", tag: "Hair Care" },
    { bg: "#e3f4d9", border: "#a9d39c", accent: "#3b7a36", tag: "Cycle Care" },
    { bg: "#fde6d6", border: "#f3c39c", accent: "#a4521f", tag: "Hormone Balance" },
];

export default function Home() {
    const [newArrivals, setNewArrivals] = useState([]);
    const [offers, setOffers] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const coreTeam = doctors.filter((d) => d.section === "Our Core Team");
    const [featuredKits, setFeaturedKits] = useState([]);
    const [offerIdx, setOfferIdx] = useState(0);

    useEffect(() => {
        api.get("/products/new-arrivals").then((r) => setNewArrivals(r.data || [])).catch(() => {});
        api.get("/products/featured-kits").then((r) => setFeaturedKits(r.data || [])).catch(() => {});
        api.get("/offers").then((r) => setOffers(r.data || [])).catch(() => {});
        api.get("/doctors").then((r) => setDoctors(r.data || [])).catch(() => {});
    }, []);

    useEffect(() => {
        if (offers.length <= 1) return;
        const t = setInterval(() => setOfferIdx((i) => (i + 1) % offers.length), 5000);
        return () => clearInterval(t);
    }, [offers.length]);

    return (
        <div data-testid="home-page">
            {/* OFFERS CAROUSEL */}
            {offers.length > 0 && (
                <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 pt-6" data-testid="offers-carousel">
                    {offers.map((o, idx) => (
                        <div
                            key={o.id}
                            className={`rounded-2xl overflow-hidden transition-all hover-float ${idx === offerIdx ? "block" : "hidden"}`}
                            style={{ background: o.background_color || "#f5ede1" }}
                            data-testid={`offer-banner-${o.id}`}
                        >
                            <div className="grid sm:grid-cols-2 items-center gap-4 sm:gap-6 p-5 sm:p-8 lg:p-10">
                                <div>
                                    <div className="overline mb-2">Featured offer</div>
                                    <h3 className="font-serif text-[24px] sm:text-[34px] lg:text-[40px] leading-tight text-[var(--ink)] mb-2">{o.title}</h3>
                                    {o.subtitle && <p className="text-[14px] sm:text-[15px] text-[var(--body)] mb-4 max-w-md">{o.subtitle}</p>}
                                    <Link to={o.cta_link || "/shop"} className="btn btn-primary" data-testid={`offer-cta-${o.id}`}>
                                        {o.cta_text || "Shop now"} <ArrowRight size={14} />
                                    </Link>
                                </div>
                                <div className="aspect-[4/3] sm:aspect-square rounded-xl bg-white/30 overflow-hidden">
                                    {o.image_url ? <img src={o.image_url} alt={o.title} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-6xl">🌿</div>}
                                </div>
                            </div>
                        </div>
                    ))}
                    {offers.length > 1 && (
                        <div className="flex justify-center gap-1.5 mt-3">
                            {offers.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setOfferIdx(i)}
                                    className={`w-2 h-2 rounded-full transition ${i === offerIdx ? "bg-[var(--taupe)] w-6" : "bg-[var(--line)]"}`}
                                    aria-label={`Offer ${i + 1}`}
                                />
                            ))}
                        </div>
                    )}
                </section>
            )}
            {/* HERO — minimal centered */}
            <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 pt-10 pb-12 sm:pt-14 sm:pb-20 text-center">
                <p className="eyebrow rise" data-testid="hero-eyebrow">Quiet Luxury. Proven Science. Gentle Care.</p>
                <h1 className="h-display text-[34px] sm:text-[52px] md:text-[60px] lg:text-[80px] mt-4 sm:mt-6 rise rise-1 leading-[1.05]" data-testid="hero-headline">
                    Clinically-Guided Wellness for Modern Life.
                </h1>
                <p className="text-[15px] sm:text-[16px] text-[var(--body)] max-w-xl mx-auto mt-4 sm:mt-6 rise rise-2">
                    Thoughtfully formulated products backed by research, designed for long-term wellbeing.
                </p>
                <div className="mt-8 sm:mt-10 rise rise-3">
                    <Link to="/shop" className="btn btn-primary" data-testid="hero-cta">
                        Explore Formulations
                    </Link>
                </div>
            </section>

            {/* TRUST BADGES — glass card strip */}
            <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 mb-14 sm:mb-20" data-testid="trust-badges">
                <div className="card-glass py-5 sm:py-6 px-5 sm:px-8 grid grid-cols-2 lg:flex lg:flex-wrap lg:justify-around lg:items-center gap-x-4 gap-y-5 sm:gap-y-6 lg:gap-x-14">
                    {TRUST_BADGES.map((b) => (
                        <div key={b.title} className="flex items-start lg:items-center gap-2.5 sm:gap-3 min-w-0">
                            <span className="trust-circle shrink-0"><Check strokeWidth={2.4} /></span>
                            <div className="min-w-0">
                                <div className="text-[12.5px] sm:text-[14px] font-medium text-[var(--ink)] leading-snug">{b.title}</div>
                                <div className="text-[11px] sm:text-[12px] text-[var(--body)] leading-snug">{b.sub}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* FEATURED KITS — signature curated bundles (top of catalog) */}
            {featuredKits.length > 0 && (
                <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 mb-14 sm:mb-20" data-testid="featured-kits-section">
                    <div className="flex items-end justify-between mb-6 sm:mb-8 flex-wrap gap-3">
                        <div>
                            <p className="eyebrow flex items-center gap-2"><Gift size={12} /> Signature Bundles</p>
                            <h2 className="font-serif text-[28px] sm:text-[36px] lg:text-[42px] text-[var(--ink)] mt-1">Featured Kits</h2>
                            <p className="text-[13px] text-[var(--body)] mt-1 max-w-md">
                                Curated rituals that pair perfectly — designed for compounding benefits.
                            </p>
                        </div>
                        <Link to="/shop?category=Kits" className="text-[13px] uppercase tracking-[0.18em] text-[var(--taupe)] hover:text-[var(--ink)] flex items-center gap-1" data-testid="featured-kits-see-all">
                            See all kits <ArrowRight size={14} />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
                        {featuredKits.map((kit, idx) => {
                            const p = KIT_PALETTES[idx % KIT_PALETTES.length];
                            return (
                                <Link
                                    key={kit.id}
                                    to={`/product/${kit.id}`}
                                    className="block text-left rounded-2xl overflow-hidden hover-float transition-shadow shadow-sm hover:shadow-xl border focus:outline-none focus:ring-2 focus:ring-[var(--taupe)]"
                                    style={{ background: p.bg, borderColor: p.border }}
                                    data-testid={`featured-kit-${idx}`}
                                >
                                    <div className="grid grid-cols-[1fr_auto] gap-3 p-5 sm:p-6">
                                        <div className="min-w-0">
                                            <div className="text-[10px] uppercase tracking-[0.2em] font-medium mb-2" style={{ color: p.accent }}>
                                                {p.tag}
                                            </div>
                                            <h3 className="font-serif text-[20px] sm:text-[22px] leading-tight text-[var(--ink)] mb-2">
                                                {kit.name}
                                            </h3>
                                            <p className="text-[12.5px] text-[var(--body)] leading-relaxed line-clamp-3 mb-4">
                                                {kit.short_desc || kit.description}
                                            </p>
                                            <div className="flex items-baseline gap-2 mb-4">
                                                <span className="font-serif text-[18px] text-[var(--ink)]">₹ {Math.round(kit.price)}</span>
                                                {kit.original_price && kit.original_price > kit.price && (
                                                    <span className="text-[12px] text-[var(--body)] line-through">₹ {Math.round(kit.original_price)}</span>
                                                )}
                                            </div>
                                            <span
                                                className="inline-flex items-center gap-1.5 text-[12px] font-medium uppercase tracking-[0.16em] px-3 py-1.5 rounded-full"
                                                style={{ background: "#fff", color: p.accent, border: `1px solid ${p.border}` }}
                                                data-testid={`featured-kit-cta-${idx}`}
                                            >
                                                <Gift size={12} /> View kit details
                                            </span>
                                        </div>
                                        <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-xl bg-white/60 overflow-hidden shrink-0 grid place-items-center">
                                            {kit.images?.[0] ? (
                                                <img src={kit.images[0]} alt={kit.name} className="w-full h-full object-cover" />
                                            ) : (
                                                <span className="text-5xl">{kit.emoji || "🎁"}</span>
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* NEW ARRIVALS */}
            {newArrivals.length > 0 && (
                <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 mb-16 sm:mb-24" data-testid="new-arrivals-section">
                    <div className="flex items-end justify-between mb-6 sm:mb-8 flex-wrap gap-3">
                        <div>
                            <p className="eyebrow flex items-center gap-2"><Sparkles size={12} /> Just In</p>
                            <h2 className="font-serif text-[28px] sm:text-[36px] lg:text-[42px] text-[var(--ink)] mt-1">New Arrivals</h2>
                        </div>
                        <Link to="/shop" className="text-[13px] uppercase tracking-[0.18em] text-[var(--taupe)] hover:text-[var(--ink)] flex items-center gap-1" data-testid="new-arrivals-see-all">
                            Shop all <ArrowRight size={14} />
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 sm:gap-x-8 gap-y-10 sm:gap-y-14">
                        {newArrivals.slice(0, 4).map((p) => (
                            <div key={p.id} className="relative">
                                <span className="absolute -top-2 left-2 z-10 text-[10px] uppercase tracking-[0.16em] bg-[var(--taupe)] text-white px-2 py-0.5 rounded-full">New</span>
                                <ProductCard product={p} />
                            </div>
                        ))}
                    </div>
                    <div className="text-center mt-10">
                        <Link to="/shop" className="btn btn-outline" data-testid="home-shop-all-cta">
                            Shop all products <ArrowRight size={14} />
                        </Link>
                    </div>
                </section>
            )}

            {/* TRUSTED BY EXPERTS */}
            <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 mb-16 sm:mb-24" data-testid="experts-section">
                <div className="card-glass p-6 sm:p-8 lg:p-12">
                    <div className="text-center mb-8 sm:mb-10">
                        <p className="eyebrow">Our Core Team</p>
                        <h2 className="h-display text-[26px] sm:text-[32px] lg:text-[40px] mt-2">The People Behind UCare</h2>
                        <p className="text-[14px] text-[var(--body)] mt-3 max-w-md mx-auto">Every formulation is guided by our core team of industry veterans and practitioners.</p>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 max-w-2xl mx-auto gap-4 sm:gap-8 mb-8 sm:mb-10">
                        {(coreTeam.length > 0 ? coreTeam : DOCTORS).slice(0, 3).map((d) => (
                            <div key={d.id || d.name} className="text-center" data-testid={`doctor-${(d.id || d.name).slice(0, 20)}`}>
                                <div className="aspect-square rounded-full overflow-hidden bg-gradient-to-br from-[var(--bg-alt)] to-[var(--rose)] mb-2 sm:mb-3 mx-auto max-w-[140px]">
                                    {d.image_url && <img src={d.image_url} alt={d.name} className="w-full h-full object-cover" />}
                                </div>
                                <div className="text-[12px] sm:text-[14px] font-medium text-[var(--ink)] leading-tight">{d.name}</div>
                                <div className="text-[9px] sm:text-[10.5px] uppercase tracking-[0.16em] text-[var(--body)] mt-1">{d.role_title || d.specialty || d.spec}</div>
                                {d.credentials && <div className="text-[9px] text-[var(--body)] mt-0.5 italic">{d.credentials}</div>}
                            </div>
                        ))}
                    </div>
                    <div className="flex flex-wrap justify-center gap-3">
                        <Link to="/the-board" className="btn btn-outline" data-testid="meet-board-cta">
                            Meet The Board
                        </Link>
                        <Link to="/consult-doctor" className="btn btn-primary" data-testid="consult-cta">
                            Consult an Expert <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
